import { apiSlice } from "../../../features/api/apiSlice";

/**
 * The QSRBN register of institutions teaching Quantity Surveying.
 *
 * Not to be confused with `AccreditedInstitutes` in the events module — that
 * is the list of bodies that host CPD events, and a body can sit in it
 * without teaching anybody.
 */

export type QsInstitution = {
  id: number;
  name: string;
  acronym: string | null;
  state_id: number | null;
  state: string | null;
  type: string;
  type_label: string;
  programmes: string[];
  programmes_raw: string | null;
  programmes_label: string;
  accreditation_status: string;
  status_label: string;
  accreditation_note: string | null;
  hod_name: string | null;
  hod_role: string;
  hod_role_label: string;
  hod_membership_no: string | null;
  hod_phone: string | null;
  hod_email: string | null;
  /** True once the HOD has been matched to a member account. */
  hod_linked: boolean;
  hod_user: { id: number; name: string; email: string } | null;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  is_active: boolean;
  /** Only on the single-record read. */
  accredited_from?: string | null;
  accredited_to?: string | null;
  other_information?: string | null;
  hod_linked_at?: string | null;
  hod_linked_by?: string | null;
  graduate_outputs?: { year: number; graduates: number | null; programme: string | null }[];
};

export type HodCandidate = {
  user_id: number;
  name: string;
  email: string;
  membership_no: string | null;
  phone: string | null;
  matched_on: string;
  confidence: string;
};

export type QsFilters = {
  q?: string;
  state_id?: number | string;
  type?: string;
  status?: string;
  programme?: string;
  /** linked = matched to a member · unlinked = named but not matched · none = no HOD named */
  hod?: "linked" | "unlinked" | "none" | "";
  active?: "yes" | "no" | "";
  page?: number;
  per_page?: number;
};

export function qsParams(filters: QsFilters): Record<string, any> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined),
  );
}

export const qsInstitutionsApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    getQsInstitutions: b.query<
      { data: QsInstitution[]; meta: any; pagination: any },
      QsFilters | void
    >({
      query: (filters) => ({
        url: "/admin/qs-institutions",
        params: qsParams((filters ?? {}) as QsFilters),
      }),
      transformResponse: (r: any) => ({
        data: r?.data ?? [],
        meta: r?.meta ?? {},
        pagination: r?.pagination ?? null,
      }),
      providesTags: [{ type: "QsInstitutions" as const, id: "LIST" }],
    }),

    getQsInstitution: b.query<QsInstitution, number>({
      query: (id) => `/admin/qs-institutions/${id}`,
      transformResponse: (r: any) => r?.data ?? r,
      providesTags: (_r, _e, id) => [{ type: "QsInstitutions" as const, id }],
    }),

    createQsInstitution: b.mutation<any, Record<string, any>>({
      query: (body) => ({ url: "/admin/qs-institutions", method: "POST", body }),
      invalidatesTags: [{ type: "QsInstitutions" as const, id: "LIST" }],
    }),

    updateQsInstitution: b.mutation<any, { id: number; body: Record<string, any> }>({
      query: ({ id, body }) => ({ url: `/admin/qs-institutions/${id}`, method: "POST", body }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "QsInstitutions" as const, id: "LIST" },
        { type: "QsInstitutions" as const, id },
      ],
    }),

    deleteQsInstitution: b.mutation<any, number>({
      query: (id) => ({ url: `/admin/qs-institutions/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "QsInstitutions" as const, id: "LIST" }],
    }),

    /** Who the HOD details point at. Returns candidates, never a decision. */
    getHodCandidates: b.query<
      { data: HodCandidate[]; meta: { message: string | null; searched: any } },
      { id: number; membership_no?: string; email?: string; phone?: string }
    >({
      query: ({ id, ...params }) => ({
        url: `/admin/qs-institutions/${id}/hod/candidates`,
        params: qsParams(params as any),
      }),
      transformResponse: (r: any) => ({ data: r?.data ?? [], meta: r?.meta ?? {} }),
    }),

    /** Links the member AND grants them the `hod` role, on top of their existing roles. */
    linkHod: b.mutation<any, { id: number; user_id: number }>({
      query: ({ id, user_id }) => ({
        url: `/admin/qs-institutions/${id}/hod`,
        method: "POST",
        body: { user_id },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: "QsInstitutions" as const, id: "LIST" },
        { type: "QsInstitutions" as const, id },
      ],
    }),

    unlinkHod: b.mutation<any, number>({
      query: (id) => ({ url: `/admin/qs-institutions/${id}/hod`, method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        { type: "QsInstitutions" as const, id: "LIST" },
        { type: "QsInstitutions" as const, id },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetQsInstitutionsQuery,
  useGetQsInstitutionQuery,
  useCreateQsInstitutionMutation,
  useUpdateQsInstitutionMutation,
  useDeleteQsInstitutionMutation,
  useLazyGetHodCandidatesQuery,
  useLinkHodMutation,
  useUnlinkHodMutation,
} = qsInstitutionsApi;
